/**
 * Locale helpers and the few texts of the launcher (loader bundle). The full panel dictionaries are in ./i18n, which
 * spreads these: the loader imports only this module and stays small.
 */
export const launcherIt = {
  launcherOpen: 'Apri la chat con l\'assistente',
  launcherClose: 'Chiudi la chat',
  launcherUnread: 'Apri la chat: {n} messaggi non letti',
  launcherLabel: 'Hai una domanda?',
  teaserDefault: 'Ciao! Se cerchi qualcosa in particolare, chiedimi pure: ti aiuto a trovarlo.',
  teaserProduct: 'Hai un dubbio su questo prodotto? Chiedimi pure.',
  teaserClose: 'Chiudi il messaggio',
};

export type LauncherStrings = typeof launcherIt;

export const launcherEn: LauncherStrings = {
  launcherOpen: 'Open chat with the assistant',
  launcherClose: 'Close chat',
  launcherUnread: 'Open chat: {n} unread messages',
  launcherLabel: 'Any questions?',
  teaserDefault: 'Hi! If you are looking for something specific, just ask: I will help you find it.',
  teaserProduct: 'Not sure about this product? Just ask.',
  teaserClose: 'Dismiss message',
};

export const launcherDe: LauncherStrings = {
  launcherOpen: 'Chat mit dem Assistenten öffnen',
  launcherClose: 'Chat schließen',
  launcherUnread: 'Chat öffnen: {n} ungelesene Nachrichten',
  launcherLabel: 'Haben Sie eine Frage?',
  teaserDefault: 'Hallo! Wenn Sie etwas Bestimmtes suchen, fragen Sie mich einfach: Ich helfe Ihnen gern weiter.',
  teaserProduct: 'Fragen zu diesem Produkt? Fragen Sie mich einfach.',
  teaserClose: 'Nachricht schließen',
};

export const launcherFr: LauncherStrings = {
  launcherOpen: 'Ouvrir le chat avec l\'assistant',
  launcherClose: 'Fermer le chat',
  launcherUnread: 'Ouvrir le chat : {n} messages non lus',
  launcherLabel: 'Une question ?',
  teaserDefault: 'Bonjour ! Vous cherchez quelque chose en particulier ? Demandez-moi, je vous aide à le trouver.',
  teaserProduct: 'Une question sur ce produit ? Demandez-moi.',
  teaserClose: 'Fermer le message',
};

export const launcherEs: LauncherStrings = {
  launcherOpen: 'Abrir el chat con el asistente',
  launcherClose: 'Cerrar el chat',
  launcherUnread: 'Abrir el chat: {n} mensajes sin leer',
  launcherLabel: '¿Tienes alguna pregunta?',
  teaserDefault: '¡Hola! Si buscas algo en concreto, pregúntame: te ayudo a encontrarlo.',
  teaserProduct: '¿Tienes dudas sobre este producto? Pregúntame.',
  teaserClose: 'Cerrar el mensaje',
};

/** UI languages of the widget; any other language falls back to Italian (the primary store language). */
export const UI_LOCALES = ['it', 'en', 'de', 'fr', 'es'] as const;
export type UiLocale = (typeof UI_LOCALES)[number];

const launcherDictionaries: Record<UiLocale, LauncherStrings> = { it: launcherIt, en: launcherEn, de: launcherDe, fr: launcherFr, es: launcherEs };

/** Launcher texts in the UI locale (loader side; the panel uses `getStrings` from ./i18n). */
export function getLauncherStrings(locale: string): LauncherStrings {
  return launcherDictionaries[pickLocale(locale)];
}

/** Language part of a locale (`de-CH`, `pt_BR`, `EN`). */
export function languageOf(locale: string): string {
  return locale.trim().toLowerCase().split(/[-_]/)[0] ?? '';
}

/** First candidate whose language has a UI dictionary; Italian when none. */
export function pickLocale(...candidates: (string | undefined | null)[]): UiLocale {
  for (const c of candidates) {
    if (!c) continue;
    const lang = languageOf(c);
    if ((UI_LOCALES as readonly string[]).includes(lang)) return lang as UiLocale;
  }
  return 'it';
}

/** UI locales whose panel dictionary is not in assistant-panel.js but in dist/assistant-locales.js. */
export function needsLocalePack(locale: UiLocale): boolean {
  return locale === 'de' || locale === 'fr' || locale === 'es';
}

/** BCP 47 tag for `Intl` date/number formatting of a UI locale. */
export function intlLocale(locale: UiLocale): string {
  return { it: 'it-IT', en: 'en-GB', de: 'de-DE', fr: 'fr-FR', es: 'es-ES' }[locale] ?? 'it-IT';
}

/** `lang` of the host page (`<html lang="it">`, set by Shopify/Woo themes from the store language). */
export function hostDocumentLang(): string | undefined {
  try {
    const l = typeof document !== 'undefined' ? document.documentElement?.getAttribute('lang') : null;
    return l && l.trim() ? l.trim() : undefined;
  } catch {
    return undefined;
  }
}

function browserLang(): string | undefined {
  return typeof navigator !== 'undefined' ? navigator.language : undefined;
}

/**
 * UI locale precedence: merchant override (`config.locale`) → host page `<html lang>` → tenant locale (from
 * `/v1/session`) → browser language. The browser comes last on purpose: an Italian store shows an Italian widget
 * even on a phone set to English.
 */
export function resolveLocale(configLocale?: string | null, tenantLocale?: string | null): UiLocale {
  return pickLocale(configLocale, hostDocumentLang(), tenantLocale, browserLang());
}

/**
 * Locale sent to the API (`POST /v1/session` `locale`): the store-side choice only (override, page lang, tenant locale
 * cached from a previous session). When none is known it is omitted and the server decides.
 */
export function requestedLocale(configLocale?: string | null, cachedTenantLocale?: string | null): string | undefined {
  const l = (configLocale && String(configLocale).trim()) || hostDocumentLang() || (cachedTenantLocale && String(cachedTenantLocale).trim()) || undefined;
  return l ? l.slice(0, 16) : undefined;
}

/** Replaces `{name}` placeholders. */
export function fmt(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

